import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { usePageMeta } from '../hooks/usePageMeta'
import { useJsonLd } from '../hooks/useJsonLd'
import { pageMeta } from '../seo/pageMeta'
import posts from '../content/blog/posts.json'
import { formatPostDate } from './Blog'

const postFiles = import.meta.glob('../content/blog/*.md', { query: '?raw', import: 'default' })

// Article typography: prose defaults tuned to the site's palette. Embedded HTML
// design blocks in post markdown opt out with `not-prose`.
export const PROSE_CLASSES =
  'prose prose-gray dark:prose-invert lg:prose-lg max-w-none prose-headings:tracking-tight ' +
  'prose-a:text-primary-600 dark:prose-a:text-primary-400 ' +
  'prose-code:before:content-none prose-code:after:content-none prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:font-medium ' +
  'prose-pre:rounded-xl prose-img:rounded-xl ' +
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:font-normal'

// Keyword-anchored links from every post to the pages that should rank for
// those terms. scripts/prerender.mjs renders the same block into the static
// HTML — keep the two in step.
export const KEYWORD_LINKS = [
  { to: '/', anchor: 'parallel AI coding agents' },
  { to: '/products', anchor: 'AI code review' },
  { to: '/estimate', anchor: 'free AI project estimator' },
]

function RelatedPosts({ currentSlug }: { currentSlug: string }) {
  const related = posts.filter((p) => p.slug !== currentSlug).slice(0, 3)

  return (
    <aside className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
      {related.length > 0 && (
        <>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Related posts</h2>
          <ul className="space-y-4 mb-10">
            {related.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
                >
                  {p.title}
                </Link>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{p.description}</p>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        More from Factory Nexus:{' '}
        {KEYWORD_LINKS.map((l, i) => (
          <span key={l.to}>
            {i > 0 && ' · '}
            <Link to={l.to} className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
              {l.anchor}
            </Link>
          </span>
        ))}
      </p>
    </aside>
  )
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const post = posts.find((p) => p.slug === slug)
  const [content, setContent] = useState<string | null>(null)

  usePageMeta({
    title: post ? `${post.title} — Factory Nexus Blog` : pageMeta('/blog').title,
    description: post?.description ?? pageMeta('/blog').description,
    path: post ? `/blog/${post.slug}` : '/blog',
  })

  useJsonLd(
    post
      ? {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.description,
          datePublished: post.date,
          url: `https://factory-nexus.ai/blog/${post.slug}`,
          author: { '@type': 'Organization', name: 'Factory Nexus' },
        }
      : null,
  )

  useEffect(() => {
    if (!post) return
    const loader = postFiles[`../content/blog/${post.slug}.md`]
    if (!loader) return
    loader().then((raw) => setContent(raw as string))
  }, [post])

  if (!post) {
    return (
      <div className="min-h-screen text-gray-900 dark:text-gray-100">
        <PublicNavBar />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h1 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">Post not found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            That post doesn&apos;t exist (or was moved).
          </p>
          <Link to="/blog" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
            &larr; All posts
          </Link>
        </div>
        <PublicFooter />
      </div>
    )
  }

  const minutes =
    content !== null ? Math.max(1, Math.round(content.trim().split(/\s+/).length / 220)) : null

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <Link
          to="/blog"
          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
        >
          &larr; All posts
        </Link>

        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-6 [text-wrap:balance] text-gray-900 dark:text-white">
          {post.title}
        </h1>
        <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">
          <time dateTime={post.date}>{formatPostDate(post.date)}</time>
          {minutes !== null && <span> &middot; {minutes} min read</span>}
        </p>
        <div className="h-1 w-16 rounded-full bg-gradient-to-r from-primary-600 to-orange-500 mt-6 mb-10" />

        {content === null ? (
          <div className="animate-pulse space-y-4" aria-hidden="true">
            <div className="h-4 rounded bg-gray-200 dark:bg-gray-800 w-full" />
            <div className="h-4 rounded bg-gray-200 dark:bg-gray-800 w-11/12" />
            <div className="h-4 rounded bg-gray-200 dark:bg-gray-800 w-4/5" />
          </div>
        ) : (
          <article className={PROSE_CLASSES}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {content}
            </ReactMarkdown>
          </article>
        )}

        <RelatedPosts currentSlug={post.slug} />

        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
          <Link
            to="/blog"
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
          >
            &larr; All posts
          </Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  )
}
